/**
 * Unified Event Bus System
 * Browser-compatible event system for all game modules
 * Replaces multiple ad-hoc implementations (Node events, Phaser.EventEmitter, custom Maps)
 */

import type { EventPayloadMap } from './EventBus/EventPayloadMap';
export type { EventPayloadMap } from './EventBus/EventPayloadMap';
import type { EVENTS } from './EventBus/events';
export { EVENTS } from './EventBus/events';

export type EventCallback = (...args: any[]) => void;
// Storage erases event-specific tuples; typed entry points assert that boundary.
// Dynamic subscribers retain responsibility for their own payload contract.
type StoredEventCallback = (...args: unknown[]) => void;
export type Unsubscribe = () => void;

export class EventBus {
  private static instance: EventBus;
  public readonly id = Math.random().toString(36).slice(2, 9);
  private listeners = new Map<string, Set<StoredEventCallback>>();
  private debugMode = false;
  private traceMode = false;
  private eventHistory: Array<{ event: string; timestamp: number; args: unknown[] }> = [];
  private maxHistory = 100;

  // Trace mode: track dead events (emitted with 0 listeners)
  private deadEvents: Array<{ event: string; timestamp: number }> = [];
  private maxDeadEvents = 50;

  // Sticky events: last-args cache for replay-on-subscribe. Events emitted
  // via emitSticky() store their latest args here; handlers registered AFTER
  // the emit get called immediately with the cached value. Use for boot-time
  // events that fire before React mounts (e.g. LEVEL_LOADED).
  private stickyValues = new Map<string, unknown[]>();

  // Critical events that ALWAYS warn when emitted with 0 listeners (even
  // without trace mode). Restricted to events whose absence breaks combat
  // CORRECTNESS (silent zero-damage if missed), NOT events whose absence
  // breaks UI display only.
  //
  // `combat:targetChanged` was previously in this set but its sole listener
  // is `TargetInfoPanel`, which is gated by `DevConfig.systems.targetInfo`
  // — and in the `live-lobby` preset (DJ's canonical streaming + social
  // tier) `targetInfo: false` is intentional. Firing the warn there was
  // the actual-bug-vs-by-design conflation per `feedback_ssot_qualifier.md`
  // — SSoT-by-concern violation. Demoted 2026-04-26; if the panel breaks
  // a future contract the trace-mode path (line 126) still surfaces it.
  private static CRITICAL_EVENTS = new Set([
    'combat:melee_hit', 'combat:area_hit', 'combat:npcDamaged',
    'combat:playerAttack',
  ]);

  // Listener registration timeline (trace mode)
  private listenerTimeline: Array<{ action: 'on' | 'off'; event: string; timestamp: number; listenerCount: number }> = [];
  private maxTimeline = 200;

  // Per-event rate tracking — always on, low overhead (one array push +
  // bounded evict per emit). Powers `getEventStats()` so the Event Rate
  // debug tool can flag events emitting more often than expected (a signal
  // of double-fires / runaway loops). Cap the ring at 64 entries per event
  // — at >64 emits in a 1s window, oldest entries roll off and the count
  // saturates, which is fine: we only care that "it's a lot."
  private static MAX_RATE_SAMPLES = 64;
  private static RATE_WINDOW_MS = 1000;
  private rateSamples = new Map<string, number[]>();
  private lastEmitMs = new Map<string, number>();
  private lastHandlerCount = new Map<string, number>();

  static getInstance(): EventBus {
    // Use a globalThis-backed singleton so Vite HMR / duplicate module
    // evaluation does not create split EventBus instances. When the module
    // reloads, the new class sees the existing global instance and reuses it,
    // keeping listeners (e.g. RemoteCastVFXHandler) on the same bus as emitters
    // (e.g. NetworkSyncSystem).
    const g = globalThis as any;
    if (g.__HELIX_EVENTBUS_INSTANCE__) {
      return g.__HELIX_EVENTBUS_INSTANCE__;
    }
    if (!this.instance) {
      this.instance = new EventBus();
    }
    g.__HELIX_EVENTBUS_INSTANCE__ = this.instance;
    if (typeof window !== 'undefined') {
      (window as any).__EVENT_BUS__ = this.instance;
    }
    return this.instance;
  }

  /** Reset singleton instance (for testing only) */
  static resetInstance(): void {
    this.instance?.clear();
    this.instance = undefined as any;
    (globalThis as any).__HELIX_EVENTBUS_INSTANCE__ = undefined;
  }

  // ===== Typed public API (T3.3 E2-B, 2026-06-22) =====
  // Every event flows through EventName + EventPayloadMap, so `emit(EVENTS.X,
  // payload)` is compile-checked for BOTH the name and the payload shape — the
  // silent-wrong-payload bug class (a handler reading `.damage` when the emitter
  // sent `.dmg`) the runtime dead-events tracker could never catch. The variadic
  // `...args: EmitArgs<K>` collapses to `[]` for void-payload events and
  // `[payload]` otherwise, so void events stay callable as `emit(EVENTS.X)`.

  /** Subscribe to a typed event. Returns unsubscribe function. */
  on<K extends EventName>(event: K, handler: EventHandler<K>): Unsubscribe {
    return this.onImpl(event, handler as StoredEventCallback);
  }

  /** Subscribe once; auto-unsubscribe after the first emit. */
  once<K extends EventName>(event: K, handler: EventHandler<K>): Unsubscribe {
    return this.onceImpl(event, handler as StoredEventCallback);
  }

  /** Unsubscribe a handler from a typed event. */
  off<K extends EventName>(event: K, handler: EventHandler<K>): void {
    this.offImpl(event, handler as StoredEventCallback);
  }

  /** Emit a typed event to all subscribers. */
  emit<K extends EventName>(event: K, ...args: EmitArgs<K>): void {
    this.emitImpl(event, args);
  }

  /** Emit a typed event with replay-on-subscribe (see emitStickyImpl). */
  emitSticky<K extends EventName>(event: K, ...args: EmitArgs<K>): void {
    this.emitStickyImpl(event, args);
  }

  // ----- Dynamic-key escape hatch -----
  // The ONLY untyped emit/subscribe path. Reserved for genuinely server-driven
  // event names that cannot be known statically: the `net:message:${type}`
  // relay (NetworkSyncSystem) forwarding arbitrary Colyseus message types, and
  // the `guild:${type}` fan-out (GuildManager). The arch-test allowlists these
  // call sites; every other emit/subscribe MUST use the typed EventName API.

  /** Dynamic-key emit. See escape-hatch note. */
  emitDynamic(event: string, ...args: any[]): void {
    this.emitImpl(event, args);
  }

  /** Dynamic-key subscribe. See escape-hatch note. */
  onDynamic(event: string, handler: EventCallback): Unsubscribe {
    return this.onImpl(event, handler);
  }

  /** Dynamic-key subscribe-once. See escape-hatch note. */
  onceDynamic(event: string, handler: EventCallback): Unsubscribe {
    return this.onceImpl(event, handler);
  }

  /** Dynamic-key unsubscribe. See escape-hatch note. */
  offDynamic(event: string, handler: EventCallback): void {
    this.offImpl(event, handler);
  }

  /** Dynamic-key sticky emit. See escape-hatch note. */
  emitStickyDynamic(event: string, ...args: any[]): void {
    this.emitStickyImpl(event, args);
  }

  /** Subscribe to an event (internal string-keyed impl). */
  private onImpl(event: string, handler: StoredEventCallback): Unsubscribe {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(handler);

    const count = this.listeners.get(event)!.size;
    if (this.debugMode) {
      console.log(`[EventBus] +${event} (${count} listeners)`);
    }
    if (this.traceMode) {
      this.listenerTimeline.push({ action: 'on', event, timestamp: Date.now(), listenerCount: count });
      if (this.listenerTimeline.length > this.maxTimeline) this.listenerTimeline.shift();
    }

    // Sticky replay: if event was emitted via emitSticky() before this
    // subscription, fire the handler with the cached args immediately.
    const sticky = this.stickyValues.get(event);
    if (sticky !== undefined) {
      try {
        handler(...sticky);
      } catch (err) {
        console.error(`[EventBus] sticky replay error for "${event}":`, err);
      }
    }

    return () => this.offImpl(event, handler);
  }

  /** Subscribe once, auto-unsubscribe after first emit (internal impl) */
  private onceImpl(event: string, handler: StoredEventCallback): Unsubscribe {
    const wrapped = (...args: unknown[]) => {
      handler(...args);
      unsubscribe();
    };
    const unsubscribe = this.onImpl(event, wrapped);
    return unsubscribe;
  }

  /** Unsubscribe from an event (internal impl) */
  private offImpl(event: string, handler: StoredEventCallback): void {
    const handlers = this.listeners.get(event);
    if (handlers) {
      handlers.delete(handler);
      if (handlers.size === 0) {
        this.listeners.delete(event);
      }
    }

    const count = handlers?.size || 0;
    if (this.debugMode) {
      console.log(`[EventBus] -${event} (${count} listeners)`);
    }
    if (this.traceMode) {
      this.listenerTimeline.push({ action: 'off', event, timestamp: Date.now(), listenerCount: count });
      if (this.listenerTimeline.length > this.maxTimeline) this.listenerTimeline.shift();
    }
  }

  /** Emit an event to all subscribers (internal string-keyed impl) */
  private emitImpl(event: string, args: unknown[]): void {
    const handlers = this.listeners.get(event);

    // Rate tracking (always on — cheap, drives Event Rate debug tool).
    this.recordEmit(event, handlers ? handlers.size : 0);

    // Track dead events (emitted with no listeners)
    if (!handlers || handlers.size === 0) {
      // Critical combat events always warn — these cause silent zero-damage if missed
      if (EventBus.CRITICAL_EVENTS.has(event)) {
        console.warn(`[EventBus] DEAD CRITICAL EVENT: "${event}" emitted with 0 listeners — combat damage will not register`);
        (globalThis as any).__errorToast?.('warn', `Dead event: ${event} — no listeners`);
      } else if (this.traceMode) {
        console.warn(`[EventBus] ⚠️ DEAD EVENT: "${event}" emitted with 0 listeners`);
      }
      this.deadEvents.push({ event, timestamp: Date.now() });
      if (this.deadEvents.length > this.maxDeadEvents) this.deadEvents.shift();
      return;
    }

    // Track history
    if (this.debugMode || this.traceMode) {
      this.eventHistory.push({
        event,
        timestamp: Date.now(),
        args: args.map(a => typeof a === 'object' ? '[object]' : a)
      });
      if (this.eventHistory.length > this.maxHistory) {
        this.eventHistory.shift();
      }
    }

    if (this.traceMode) {
      console.log(`[EventBus] → ${event} (${handlers.size} handlers)`, args.length > 0 ? args[0] : '');
    } else if (this.debugMode) {
      console.log(`[EventBus] → ${event} (${handlers.size} handlers)`, args);
    }

    // Call handlers (use array copy in case handlers modify the set)
    const handlersArray = Array.from(handlers);
    for (const handler of handlersArray) {
      try {
        handler(...args);
      } catch (err) {
        console.error(`[EventBus] Handler error for "${event}":`, err);
        // Continue to next handler - don't let one error break others
      }
    }
  }

  /**
   * Emit with replay-on-subscribe. Current subscribers fire synchronously
   * AND the latest args are cached; any `on()` registered LATER for this
   * event also fires with the cached args immediately on subscribe.
   *
   * Use for boot-time one-shots whose subscribers may mount after the emit
   * (e.g. LEVEL_LOADED from MapSystem during boot, but React UI binds later).
   * Do NOT use for high-frequency events — the cache only holds one value
   * per event key so it's meaningless for streams.
   *
   * NOTE: unlike emit(), sticky emits do NOT record into deadEvents when no
   * current listeners exist — the cache guarantees late subscribers will
   * receive the payload, so "dead" would be misleading.
   */
  private emitStickyImpl(event: string, args: unknown[]): void {
    this.stickyValues.set(event, args);

    const handlers = this.listeners.get(event);
    this.recordEmit(event, handlers ? handlers.size : 0);

    // Track history if debug/trace enabled (same as emit())
    if (this.debugMode || this.traceMode) {
      this.eventHistory.push({
        event,
        timestamp: Date.now(),
        args: args.map(a => typeof a === 'object' ? '[object]' : a),
      });
      if (this.eventHistory.length > this.maxHistory) {
        this.eventHistory.shift();
      }
    }

    if (!handlers || handlers.size === 0) {
      // Intentionally NOT recording in deadEvents — sticky replay covers it.
      return;
    }

    if (this.traceMode) {
      console.log(`[EventBus] → ${event} (sticky, ${handlers.size} handlers)`, args.length > 0 ? args[0] : '');
    }

    const handlersArray = Array.from(handlers);
    for (const handler of handlersArray) {
      try {
        handler(...args);
      } catch (err) {
        console.error(`[EventBus] Handler error for "${event}" (sticky):`, err);
      }
    }
  }

  /** Clear a sticky value (for testing / explicit reset). */
  clearSticky(event?: string): void {
    if (event) {
      this.stickyValues.delete(event);
    } else {
      this.stickyValues.clear();
    }
  }

  /** Remove all listeners for an event, or all events if no event specified */
  clear(event?: string): void {
    if (event) {
      this.listeners.delete(event);
      this.stickyValues.delete(event);
    } else {
      this.listeners.clear();
      this.stickyValues.clear();
    }
  }

  /** Get count of listeners (for debugging) */
  listenerCount(event?: string): number {
    if (event) {
      return this.listeners.get(event)?.size ?? 0;
    }
    return Array.from(this.listeners.values()).reduce((sum, set) => sum + set.size, 0);
  }

  /** Get list of all active event names */
  getEventNames(): string[] {
    return Array.from(this.listeners.keys());
  }

  /**
   * Record an emit timestamp for rate tracking. Called from emit() + emitSticky().
   * Evicts entries older than RATE_WINDOW_MS so the ring stays bounded.
   */
  private recordEmit(event: string, handlerCount: number): void {
    const now = Date.now();
    let ring = this.rateSamples.get(event);
    if (!ring) {
      ring = [];
      this.rateSamples.set(event, ring);
    }
    ring.push(now);
    // Evict expired samples from the front. Linear scan but bounded by
    // MAX_RATE_SAMPLES (64) so it's O(64) worst case per emit.
    const cutoff = now - EventBus.RATE_WINDOW_MS;
    while (ring.length > 0 && ring[0] < cutoff) ring.shift();
    // Hard cap to keep memory predictable under pathological loops.
    if (ring.length > EventBus.MAX_RATE_SAMPLES) {
      ring.splice(0, ring.length - EventBus.MAX_RATE_SAMPLES);
    }
    this.lastEmitMs.set(event, now);
    this.lastHandlerCount.set(event, handlerCount);
  }

  /**
   * Per-event emit statistics over the rolling 1-second window.
   *
   * Returns an array sorted by `emitsLastSec` descending so the noisiest
   * events appear first. `lastHandlerCount` surfaces duplicate listeners
   * — if a single emit fires 4 handlers but you expected 1, that's a
   * subscribe-leak. `lastEmitMs` lets the UI show "Xs ago" relative time.
   *
   * Reading is non-mutating + cheap; the EventRateOverlay polls this at
   * 250ms intervals.
   */
  getEventStats(): Array<{
    name: string;
    emitsLastSec: number;
    lastHandlerCount: number;
    lastEmitMs: number;
  }> {
    const now = Date.now();
    const cutoff = now - EventBus.RATE_WINDOW_MS;
    const out: Array<{ name: string; emitsLastSec: number; lastHandlerCount: number; lastEmitMs: number }> = [];
    for (const [name, ring] of this.rateSamples.entries()) {
      let i = 0;
      while (i < ring.length && ring[i] < cutoff) i++;
      const emitsLastSec = ring.length - i;
      if (emitsLastSec === 0) continue;
      out.push({
        name,
        emitsLastSec,
        lastHandlerCount: this.lastHandlerCount.get(name) ?? 0,
        lastEmitMs: this.lastEmitMs.get(name) ?? 0,
      });
    }
    out.sort((a, b) => b.emitsLastSec - a.emitsLastSec);
    return out;
  }

  /** Enable/disable debug logging */
  setDebug(enabled: boolean): void {
    this.debugMode = enabled;
  }

  /** Check if debug mode is enabled */
  isDebugEnabled(): boolean {
    return this.debugMode;
  }

  /**
   * Enable/disable TRACE mode — verbose logging with dead-event detection.
   * Much more detailed than debug mode. Use when hunting timing bugs.
   * Can also be enabled from console: window.__EVENT_BUS__.setTrace(true)
   */
  setTrace(enabled: boolean): void {
    this.traceMode = enabled;
    if (enabled) {
      this.debugMode = true; // trace implies debug
      console.log('[EventBus] 🔍 TRACE MODE ENABLED — will log all events and warn on dead events');
      console.log(`[EventBus] Current listeners: ${this.getEventNames().length} events registered`);
    } else {
      console.log('[EventBus] Trace mode disabled');
    }
  }

  /** Check if trace mode is enabled */
  isTraceEnabled(): boolean {
    return this.traceMode;
  }

  /** Get recent event history (debug mode only) */
  getHistory(): Array<{ event: string; timestamp: number; args: any[] }> {
    return [...this.eventHistory];
  }

  /** Clear event history */
  clearHistory(): void {
    this.eventHistory = [];
  }

  /** Get dead events — events emitted with 0 listeners (trace mode) */
  getDeadEvents(): Array<{ event: string; timestamp: number }> {
    return [...this.deadEvents];
  }

  /** Get listener registration timeline (trace mode) */
  getListenerTimeline(): Array<{ action: 'on' | 'off'; event: string; timestamp: number; listenerCount: number }> {
    return [...this.listenerTimeline];
  }

  /**
   * Get a full listener report — snapshot of all events and their listener counts.
   * Useful for comparing "what's listening" vs "what's expected".
   * Can be called from console: window.__EVENT_BUS__.getListenerReport()
   */
  getListenerReport(): Record<string, number> {
    const report: Record<string, number> = {};
    for (const [event, handlers] of this.listeners) {
      report[event] = handlers.size;
    }
    return report;
  }
}

// Export type for event names
export type EventName = typeof EVENTS[keyof typeof EVENTS];

/**
 * Args tuple for emitting an event K: empty for void-payload events (so
 * `emit(EVENTS.X)` stays legal), a single `[payload]` otherwise. Exported for
 * SystemBase's typed forwarders.
 */
export type EmitArgs<K extends EventName> = EventPayloadMap[K] extends void ? [] : [payload: EventPayloadMap[K]];

/** Handler signature for a typed event K. */
export type EventHandler<K extends EventName> = (...args: EmitArgs<K>) => void;
